import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, InputBase, Button, Breadcrumbs, useMediaQuery, useTheme } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { faqs } from "./faqs";
import type { FaqCategory } from "./faqs";
import FaqAccordion from "../../components/faq-accordion/faq-accordion";
import { axiosRequestData } from "../../helpers/axios";
import ContactSuccessMessage from "./success-message";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { filterFaqs } from "../../helpers/faq-search";
import SearchBar from "../../components/search/search-bar";
import { clickedChatWithUs } from "../../components/mixPanelEvents/mixPanelEvents";
import { contactUsSearchCompleted } from "../../components/mixPanelEvents/mixPanelEvents";
import { useNavigate } from "react-router";
import BreadcrumbsNav from "../../components/navigation/breadcrumbs-nav/BreadcrumbsNav";
import type { BreadcrumbItem } from "../../components/navigation/types/BreadcrumbItem";

const wrapperSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  alignSelf: "stretch",
  width: "100%",
  //maxWidth: "1408px",
  //mx: "auto",
  paddingTop: { xs: "var(--spacing-10, 40px)", sm: "10px" },
  paddingBottom:{xs:"40px", md:"60px"},
  px: {
    xs: "15px",
    sm: "15px",
    lg: "15px",
  },
  "@media (max-width: 768px)": { paddingTop: "0px" },

};

const h3MobileSx: SxProps<Theme> = {
  color: "var(--Text-primary, #000)",
  fontFamily: '"Repro", sans-serif',
  fontSize: {
    xs: "36px",   // <= 767px (mobile)
    md: "78px",   // >= 768px (desktop)
  },
  fontStyle: "normal",
  fontWeight: 700,
  lineHeight: "120%",
  alignSelf: "stretch",
  textTransform: "uppercase",
};

const h4MobileSx: SxProps<Theme> = {
  color: "var(--Text-primary, #000)",
  fontFamily: '"Repro", sans-serif',
  fontSize: "22px",
  fontStyle: "normal",
  fontWeight: 500,
  lineHeight: "140%",
  alignSelf: "stretch",
  textTransform: "uppercase",
  letterSpacing: "-1px",
};

const body16Sx: SxProps<Theme> = {
  alignSelf: "stretch",
  color: "var(--Text-primary, #000)",
  fontFamily: '"Repro", sans-serif',
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "150%",
};

const dividerSx: SxProps<Theme> = {
  height: "1px",
  width: "100%",
  bgcolor: "var(--Text-primary, #EEEEEE)",
  mt: "var(--spacing-3, 12px)",
  alignSelf: "stretch",
};

const ctaButtonSx: SxProps<Theme> = {

  mt: "16px",
  alignSelf: "flex-start",
  display: "inline-flex",
  width:"150px",
  height: "48px",
  px: "10px",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "100px",      
  backgroundColor: "#FD3300", 
  color: "#fff",
  textTransform: "none",
  fontFamily: '"Repro", sans-serif',
  fontWeight: 700,
  fontSize: "16px",

  "&:hover": {
    backgroundColor: "#E12D00", 
  },

  "&.Mui-disabled": {
    backgroundColor: "#FD3300",
    color: "#fff",
    opacity: 0.5,
  },
  // mt: "16px",
  // alignSelf: "flex-start",
  // display: "flex",
  // height: "40px",
  // px: "var(--spacing-6, 24px)",
  // justifyContent: "center",
  // alignItems: "center",
  // gap: "1px",
  // borderRadius: "var(--Radius-rounded, 4px)",
  // background: "var(--Primary-Blue, #355ADD)",
  // color: "#fff",
  // textTransform: "none",
  // fontFamily: '"Repro", sans-serif',
  // fontWeight: 700,
  // '&:hover': {
  //   backgroundColor: '#163190 !important'
  // },
  // "&.Mui-disabled": {
  //   background: "var(--Primary-Blue, #355ADD)",
  //   color: "#fff",
  //   opacity: 0.5,
  // },
};

const ContactUs: React.FC = () => {
  const faqItems = useMemo(() => faqs, []);
  const [subject, setSubject] = useState<string>("");
  const [isSubjectFocused, setIsSubjectFocused] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [isMessageFocused, setIsMessageFocused] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string>("");
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const debouncedQuery = useDebouncedValue(searchQuery.trim(), 600);
  const debouncedQueryForTracking = useDebouncedValue(searchQuery.trim(), 10000);
  const lastTrackedQuery = useRef<string>("");
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  

  const handleClick = () => {
        window.location.href = window.location.origin;
    };


  const canSubmit = useMemo<boolean>(
    () => subject.trim().length > 0 && message.trim().length > 0,
    [subject, message]
  );

  const filteredFaqItems = useMemo<FaqCategory[]>(() => {
    return filterFaqs(faqItems, debouncedQuery);
  }, [faqItems, debouncedQuery]);

  useEffect(() => {
    if (searchQuery.trim().length === 0) {
      lastTrackedQuery.current = "";
    }
  }, [searchQuery]);

  useEffect(() => {
    if (!debouncedQueryForTracking || debouncedQueryForTracking.length < 3) return;
    if (debouncedQueryForTracking === lastTrackedQuery.current) return;
    contactUsSearchCompleted(debouncedQueryForTracking);
    lastTrackedQuery.current = debouncedQueryForTracking;
  }, [debouncedQueryForTracking]);

  const handleSearchBlur = useCallback<() => void>(() => {
    const currentQuery = searchQuery.trim();
    if (!currentQuery || currentQuery.length < 3) return;
    if (currentQuery === lastTrackedQuery.current) return;
    contactUsSearchCompleted(currentQuery);
    lastTrackedQuery.current = currentQuery;
  }, [searchQuery]);

  const handleSubmit = useCallback<
    (event: React.FormEvent<HTMLFormElement>) => Promise<void>
  >(
    async (event) => {
      event.preventDefault();
      if (!canSubmit || isSubmitting) return;
      setSubmitError("");
      setSubmitSuccess(false);
      setIsSubmitting(true);
      try {
        const response = await axiosRequestData(
          "POST",
          "/wp-json/pg/v1/contact",
          { subject, message }
        );
        const data = response?.data;
        if (data?.success) {
          setSubmitSuccess(true);
          setSubject("");
          setMessage("");
        } else {
          setSubmitError(data?.message || "Failed to send message.");
        }
      } catch (error: any) {
        const apiMessage = error?.data?.message || error?.statusText || "Request failed.";
        setSubmitError(apiMessage);
      } finally {
        setIsSubmitting(false);
      }
    },
    [canSubmit, isSubmitting, message, subject]
  );

  const breadcrumbItems: readonly BreadcrumbItem[] = [
    { label: 'Contact Us' },
  ];

  

  return (
    <Box sx={ !isMobile ? { maxWidth: "1440px", margin: "0 auto" } : { } }>
    <Box sx={wrapperSx}>
      <Box sx={{ marginTop: "0", marginBottom: "20px", alignSelf: "stretch" }}>
        <BreadcrumbsNav items={breadcrumbItems} separator="/" prefix="/" showIcon={true} />
      </Box>
      {submitSuccess ? (
        <ContactSuccessMessage dashboardHref="/" />
      ) : (
        <>
        {/* <Breadcrumbs
          aria-label="breadcrumb" 
          sx={{
          paddingBottom: "20px",
          fontFamily: '"Repro", sans-serif',
          alignSelf: "stretch",        
          display: "flex",
          justifyContent: "flex-start",

          // paddingLeft: { xs: "15px", md: "40px" },
          marginTop: {xs: '10px',md: '40px' },
          marginBottom: "-10px",

          

          "& .MuiBreadcrumbs-ol": {
            width: "100%",
            justifyContent: "flex-start",
          },

          "& li": {
            display: "flex",
            margin: 0,
            fontWeight: 400,
            fontSize: 14,
            lineHeight: "14px",
          },
          "& .MuiBreadcrumbs-separator": { margin: 0, lineHeight: 0 },
          }}
        >
          <Button
            style={{ textTransform: "none" }}
            sx={{
              "&:hover": {
                backgroundColor: "transparent",
                textDecoration: "none",
              },
              textDecoration: "none",
              cursor: "default !important",
              color: "#7B726C", 
              justifyContent: "flex-start",
              textAlign: "left",
            }}
            variant="text"
                  onClick={handleClick}>Training</Button>
          <Button
            style={{textTransform: 'none'}} sx={{
              color: "#1D1A1A",
              justifyContent: "flex-start",
              textAlign: "left",
              "&:hover": {backgroundColor: "transparent", textDecoration: 'none'},
              textDecoration: 'none',
              cursor: 'default !important'
          }} variant="text" 
                  onClick={() => navigate("/contact-us/")}>Contact Us</Button>
        </Breadcrumbs> */}
          <Typography sx={h3MobileSx}>Contact us</Typography>
          {/* <Box sx={dividerSx} /> */}

          <Typography sx={{ ...h4MobileSx, mt: "25px" }}>
            Chat With Our Support Team
          </Typography>

          <Typography sx={{ ...body16Sx, mt: "16px" }}>
            If you need assistance, our support team is just a click away. Use the
            button below to speak with a live agent directly.
          </Typography>

          <Button onClick={() => {
            clickedChatWithUs('Contact Us');
            // @ts-ignore
            window?.Intercom('show')
          }} sx={ctaButtonSx}>Chat With Us</Button>

          <Typography
            sx={{ ...h4MobileSx, color: "var(--Primary-Black, #000)", mt: "80px" }}
          >
            FAQs
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'end', justifyContent: 'space-between', gap: '16px', alignSelf: 'stretch', mt: { xs: '11px', sm: '-14px' }, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Typography
              sx={{
                ...body16Sx,
                color: "var(--Primary-Black, #000)",
                m: 0,
                alignSelf:'auto'
              }}
            >
              Find answers to common questions about our products, services, and more.
            </Typography>
            <SearchBar value={searchQuery} onChange={setSearchQuery} onBlur={handleSearchBlur} />
          </Box>
          {debouncedQuery && filteredFaqItems.length === 0 ? (
            <Box sx={{ alignSelf: 'stretch', mt: '16px', p: '12px', border: '1px dashed #CECCCC', borderRadius: '8px', color: 'var(--Primary-Black, #000)' }}>
              <Typography sx={{ fontFamily: '"Repro", sans-serif', fontSize: 16, fontWeight: 700, lineHeight: '150%' }}>
                No results found
              </Typography>
              <Typography sx={{ fontFamily: '"Repro", sans-serif', fontSize: 14, fontWeight: 400, lineHeight: '150%', mt: '4px' }}>
                Try a different keyword or clear the search.
              </Typography>
            </Box>
          ) : (
            <FaqAccordion items={filteredFaqItems} />
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', alignSelf: 'stretch' }}>
            <Typography sx={{ ...h4MobileSx, mt: "80px" }}>
              Send Us a Message
            </Typography>
            <Typography sx={{ ...body16Sx, mt: "16px" }}>
              Have questions or need assistance? Fill out our Contact Us form, and our
              team will get back to you as soon as possible!
            </Typography>
          </Box>
          <Box
            sx={{
              border: "1px solid #DFD9D5",
              borderRadius: "8px",
              alignSelf: "stretch",
              width: "100%",
              backgroundColor: "#FCFAFA",

              pt: "50px",       
              px: "110px",      
              pb: {md:"150px", xs:"16px"}, 

              mt: "16px",

              // responsive safety
              "@media (max-width: 1200px)": { px: "60px" },
              "@media (max-width: 900px)": { px: "40px" },
              "@media (max-width: 600px)": { px: "12px" },
              "@media (max-width: 768px)": { pt: "8px" }
            }}
          >
            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              name="contact"
              sx={{
                alignSelf: "stretch",
                width: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  mt: "16px",
                  display: "flex",
                  height: "50px",
                  p: "var(--spacing-2, 8px)",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: "1px",
                  alignSelf: "stretch",
                  borderRadius: "var(--Radius-rounded-lg, 8px)",
                  border: "1px solid #CECCCC",
                  background: "#FFF",
                }}
              >
                {!(isSubjectFocused || subject) && (
                  <Typography
                    sx={{
                      color: "#C1B9B9",
                      fontFamily: '"Repro Mono", sans-serif',
                      fontSize: "12px",
                      fontStyle: "normal",
                      fontWeight: 400,
                      lineHeight: "normal",
                    }}
                  >
                    SUBJECT *
                  </Typography>
                )}
                <InputBase
                  name="subject"
                  id="contact-subject"
                  value={subject}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSubject(e.target.value)}
                  onFocus={() => setIsSubjectFocused(true)}
                  onBlur={() => setIsSubjectFocused(false)}
                  fullWidth
                  sx={{
                    mt: !(isSubjectFocused || subject) ? "4px" : 0,
                    p: 0,
                    fontFamily: '"Repro", sans-serif',
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: "150%",
                  }}
                  inputProps={{ "aria-label": "Subject", "aria-required": "true" }}
                />
              </Box>
              <Box
                sx={{
                  mt: "16px",
                  display: "flex",
                  height: "420px !important",
                  p: "var(--spacing-2, 8px)",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: "1px",
                  alignSelf: "stretch",
                  borderRadius: "var(--Radius-rounded-lg, 8px)",
                  border: "1px solid #CECCCC",
                  background: "#FFF",
                }}
              >
                {!(isMessageFocused || message) && (
                  <Typography
                    sx={{
                      color: "#C1B9B9",
                      fontFamily: '"Repro Mono", sans-serif',
                      fontSize: "12px",
                      fontStyle: "normal",
                      fontWeight: 400,
                      lineHeight: "normal",
                    }}
                  >
                    YOUR MESSAGE *
                  </Typography>
                )}
                <InputBase
                  name="message"
                  id="contact-message"
                  value={message}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMessage(e.target.value)}
                  onFocus={() => setIsMessageFocused(true)}
                  onBlur={() => setIsMessageFocused(false)}
                  fullWidth
                  multiline
                  sx={{
                    mt: !(isMessageFocused || message) ? "4px" : 0,
                    p: 0,
                    height: "auto",
                    fontFamily: '"Repro", sans-serif',
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: "150%",
                    "& textarea": {
                      height: "100% !important",
                      resize: "none",
                      overflow: "auto",
                      boxSizing: "border-box",
                    },
                  }}
                  inputProps={{
                    "aria-label": "Your Message",
                    "aria-required": "true",
                  }}
                />
              </Box>
              <Button type="submit" disabled={!canSubmit || isSubmitting} sx={{...ctaButtonSx, width: "100%"}}>
                {isSubmitting ? "Sending..." : "Send Message"}
              </Button>
            </Box>
          </Box>  
        </>
      )}
    </Box>
    </Box>
  );
};

export default ContactUs;
